import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import { fetchLeaveReasonTypes } from "../../../Apis/Leave-api";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";

function LeaveReasonForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const isReadOnly = mode === "view";
  const [leaveReasonType, setLeaveReasonType] = useState([]);

  const [formData, setFormData] = useState({
    leave_reason_name: "",
    description: "",
    organization_leave_reason_type_id: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit" || mode === "view");
  const [btnLoading, setbtnLoading] = useState(false);

  let navigate = useNavigate();

  useEffect(() => {
    if (!org?.organization_id) return;

    fetchLeaveReasonTypes(org.organization_id)
      .then((data) => {
        setLeaveReasonType(data?.leavereasontype || []);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load leave reason types.");
      });
  }, [org?.organization_id]);

  useEffect(() => {
    let getdataById = async () => {
      if (!org?.organization_id || !id) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/leave-reason/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const record =
          response.data.leavereason ||
          response.data.leaveReason ||
          response.data ||
          {};
        setFormData({
          leave_reason_name: record.leave_reason_name || "",
          description: record.description || "",
          organization_leave_reason_type_id:
            record.organization_leave_reason_type_id ?? "",
        });
      } catch (err) {
        console.error(err);
        toast.error("Failed to load Leave Reason.");
      } finally {
        setLoading(false);
      }
    };

    if ((mode === "edit" || mode === "view") && id && id !== "undefined") {
      setLoading(true);
      getdataById();
    } else if (mode === "view" || mode === "edit") {
      setLoading(false);
      toast.error("Invalid Leave Reason id.");
    }
  }, [mode, id, org?.organization_id]);

  const handleChange = (e) => {
    if (isReadOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.leave_reason_name)
      errors.leave_reason_name = "Leave Reason Name is required.";

    if (!formData.organization_leave_reason_type_id)
      errors.organization_leave_reason_type_id = "Leave Reason Type is required.";

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;
    if (!validateForm()) return;
    setbtnLoading(true);

    try {
      if (mode === "edit") {
        await axios.put(
          `${MAIN_URL}/api/organizations/${org.organization_id}/leave-reason/${id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        await axios.post(
          `${MAIN_URL}/api/organizations/${org.organization_id}/leave-reason`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }
      toast.success(
        mode === "edit" ? "Leave Reason updated!" : "Leave Reason created!"
      );
      setFormErrors({});
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        const flattened = Object.fromEntries(
          Object.entries(validationErrors).map(([key, value]) => [
            key,
            Array.isArray(value) ? value[0] : value,
          ])
        );
        setFormErrors(flattened);
        const errorMessages =
          Object.values(flattened).filter(Boolean).join(" ") ||
          "Validation failed.";
        toast.error(errorMessages);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setbtnLoading(false);
    }
  };

  return (
    <Header
      mode={mode}
      updateMessage={"Leave Reason"}
      addMessage={"Leave Reason"}
      homeLink={"/organization-configration/leave-reason"}
      homeText={"Leave Reason"}
      onSubmit={isReadOnly ? undefined : handleSubmit}
      loading={loading}
      btnLoading={btnLoading}
    >
      {loading ? (
        <Grid container spacing={2}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <TextField
                  fullWidth
                  label="Leave Reason Name"
                  name="leave_reason_name"
                  value={formData.leave_reason_name}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  InputProps={{ readOnly: isReadOnly }}
                  error={!!formErrors.leave_reason_name}
                  helperText={formErrors.leave_reason_name}
                  required
                  inputProps={{ maxLength: 50 }}
                />

                <Autocomplete
                  fullWidth
                  options={leaveReasonType || []}
                  getOptionLabel={(option) =>
                    option.leave_reason_type_name || ""
                  }
                  value={
                    leaveReasonType?.find(
                      (option) =>
                        Number(option.organization_leave_reason_type_id) ===
                        Number(formData.organization_leave_reason_type_id)
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: "organization_leave_reason_type_id",
                        value:
                          newValue?.organization_leave_reason_type_id || "",
                      },
                    });
                  }}
                  disabled={isReadOnly || leaveReasonType?.length === 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Leave Reason Type"
                      error={!!formErrors.organization_leave_reason_type_id}
                      helperText={formErrors.organization_leave_reason_type_id}
                      required
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        readOnly: isReadOnly,
                      }}
                    />
                  )}
                />

                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  InputProps={{ readOnly: isReadOnly }}
                  multiline
                  rows={3}
                  error={!!formErrors.description}
                  helperText={formErrors.description}
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>

              {!isReadOnly && (
                <Grid container spacing={2} mt={2}>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={handleSubmit}
                      disabled={loading || btnLoading}
                      sx={{
                        borderRadius: 2,
                        minWidth: 120,
                        textTransform: "capitalize",
                        fontWeight: 500,
                      }}
                    >
                      {loading || btnLoading ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </Grid>

                  {mode === "edit" && (
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        onClick={() => navigate(-1)}
                        sx={{
                          borderRadius: 2,
                          minWidth: 120,
                          textTransform: "capitalize",
                          fontWeight: 500,
                          backgroundColor: "#1976d2",
                          "&:hover": { backgroundColor: "#115293" },
                        }}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default LeaveReasonForm;
